import { useState, useEffect } from 'react';
import { Modal } from '../../ui';
import { promptRefinementService, type PreviewResponse } from '../../../services/promptRefinementService';
import { llmModelService } from '../../../services/llmModelService';
import type { LLMModel } from '../../../types/llmModel';
import { Loader2 } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (params: {
    llm_config_id?: string;
    suggestion_targets: string[];
    max_retries: number;
  }) => void;
  runId: string;
  selectedFields: string[];
  errorRateThreshold: number;
  defaultModelName?: string;
}

export function GenerateConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  runId,
  selectedFields,
  errorRateThreshold,
  defaultModelName,
}: Props) {
  const [models, setModels] = useState<LLMModel[]>([]);
  const [selectedModelName, setSelectedModelName] = useState(defaultModelName || '');
  const [targets, setTargets] = useState<string[]>(['schema']);
  const [maxRetries, setMaxRetries] = useState(2);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [showUserPrompt, setShowUserPrompt] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadModels();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && selectedFields.length > 0) {
      loadPreview();
    }
  }, [isOpen, selectedFields, selectedModelName, targets, errorRateThreshold]);

  const loadModels = async () => {
    try {
      const data = await llmModelService.getAll(true);
      setModels(data);
      if (!selectedModelName && data.length > 0) {
        const match = defaultModelName ? data.find(m => m.model_name === defaultModelName) : null;
        setSelectedModelName(match?.model_name || data[0].model_name);
      }
    } catch (err) {
      console.error('Error loading models:', err);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const data = await promptRefinementService.preview({
        run_id: runId,
        selected_fields: selectedFields,
        suggestion_targets: targets,
        error_rate_threshold: errorRateThreshold,
      });
      setPreview(data);
    } catch (err) {
      console.error('Error loading preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleTarget = (target: string) => {
    setTargets(prev => {
      if (prev.includes(target)) {
        const next = prev.filter(t => t !== target);
        return next.length === 0 ? prev : next; // Don't allow empty
      }
      return [...prev, target];
    });
  };

  const handleConfirm = () => {
    // Find the LLM config that matches the selected model — we pass model name, backend resolves
    onConfirm({
      suggestion_targets: targets,
      max_retries: maxRetries,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirmer la génération de suggestions" size="lg">
      <div className="space-y-4">
        {/* Model selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Modèle LLM</label>
          <select
            value={selectedModelName}
            onChange={(e) => setSelectedModelName(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
          >
            {models.map(m => (
              <option key={m.id} value={m.model_name}>
                {m.display_name} ({m.provider})
              </option>
            ))}
          </select>
        </div>

        {/* Target selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type de suggestions</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={targets.includes('schema')}
                onChange={() => toggleTarget('schema')}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm">Schéma (field definitions)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={targets.includes('prompt')}
                onChange={() => toggleTarget('prompt')}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm">Prompt (règles globales)</span>
            </label>
          </div>
        </div>

        {/* Max retries */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max retries</label>
          <input
            type="number"
            value={maxRetries}
            onChange={(e) => setMaxRetries(Math.max(0, Math.min(5, parseInt(e.target.value) || 0)))}
            min={0}
            max={5}
            className="w-20 border rounded px-3 py-2 text-sm"
          />
        </div>

        {/* Estimation */}
        {loadingPreview ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin" />
            Calcul de l'estimation...
          </div>
        ) : preview && (
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-700">Estimation</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded p-3 border">
                <div className="text-xs text-gray-500">Champs</div>
                <div className="text-lg font-semibold">{preview.fields_count}</div>
              </div>
              <div className="bg-white rounded p-3 border">
                <div className="text-xs text-gray-500">Tokens input</div>
                <div className="text-lg font-semibold">{preview.estimated_input_tokens.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded p-3 border">
                <div className="text-xs text-gray-500">Tokens output (est.)</div>
                <div className="text-lg font-semibold">{preview.estimated_output_tokens.toLocaleString()}</div>
              </div>
              <div className="bg-white rounded p-3 border">
                <div className="text-xs text-gray-500">Coût estimé</div>
                <div className="text-lg font-semibold">${preview.estimated_cost.toFixed(4)}</div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Modèle : {preview.model_display_name} — Temps estimé : ~{preview.estimated_time_seconds}s
            </div>
            <div className="text-xs text-gray-400">
              Champs : {preview.field_names.join(', ')}
            </div>
          </div>
        )}

        {/* Prompt preview */}
        {preview && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowSystemPrompt(!showSystemPrompt)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showSystemPrompt ? 'Masquer' : 'Voir'} le system prompt
            </button>
            {showSystemPrompt && (
              <pre className="bg-gray-900 text-gray-100 text-xs p-4 rounded-lg max-h-48 overflow-auto whitespace-pre-wrap">
                {preview.system_prompt}
              </pre>
            )}

            <button
              type="button"
              onClick={() => setShowUserPrompt(!showUserPrompt)}
              className="text-sm text-blue-600 hover:underline"
            >
              {showUserPrompt ? 'Masquer' : 'Voir'} le prompt utilisateur
            </button>
            {showUserPrompt && (
              <pre className="bg-gray-900 text-gray-100 text-xs p-4 rounded-lg max-h-64 overflow-auto whitespace-pre-wrap">
                {preview.user_prompt}
              </pre>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={loadingPreview || !preview}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50 text-sm"
          >
            Confirmer et envoyer au LLM
          </button>
        </div>
      </div>
    </Modal>
  );
}
